import moment from 'moment-timezone';
import type { CandidateAvailabilitySlot } from './events/buildCandidateAvailabilityEvents';

/**
 * Parse a calendar focus date for FullCalendar's `gotoDate` / `initialDate`.
 *
 * A date-only `YYYY-MM-DD` string must be read in the calendar's `timezone`,
 * NOT as UTC midnight. `new Date('2026-07-12')` is UTC midnight, and the
 * calendar is mounted with `timeZone={timezone}` + `firstDay={0}` (Sun–Sat), so
 * that instant re-projects a day earlier at any negative UTC offset (US zones) —
 * landing the view on the previous week. This surfaced as the "Next week" /
 * "Next 2 weeks" find-time presets (which always anchor on a Sunday) opening the
 * wrong week for US accounts while the slots were applied correctly.
 *
 * A `Date` (or a full ISO timestamp string carrying a zone/offset) is already an
 * unambiguous instant, so it's returned/parsed without a UTC-midnight guess.
 */
export const parseFocusDate = (date: string | Date, timezone: string): Date =>
  typeof date === 'string' ? moment.tz(date, timezone).toDate() : date;

/**
 * ENG-3903 — the week the calendar should open on when the candidate has
 * submitted their availability.
 *
 * Opening on today made the recruiter scroll forward to whatever week the
 * candidate actually offered. Land on the first submitted window instead.
 * Windows that have already ended are skipped (a request opened days after
 * it was answered shouldn't park the view on a dead day); if every window
 * is in the past, the earliest one is still better than today.
 *
 * Returns the raw `start_at` — a full ISO instant, so the caller can hand
 * it to `parseFocusDate` unambiguously.
 */
export const firstAvailabilityFocusDate = (
  slots: ReadonlyArray<CandidateAvailabilitySlot> | undefined,
  nowMs: number = Date.now()
): string | undefined => {
  if (!slots?.length) return undefined;

  const valid = slots.filter(
    (s) => s?.start_at && moment(s.start_at).isValid()
  );
  if (!valid.length) return undefined;

  const earliestStart = (list: ReadonlyArray<CandidateAvailabilitySlot>) =>
    list.reduce((best, s) =>
      moment(s.start_at).valueOf() < moment(best.start_at).valueOf() ? s : best
    ).start_at;

  const upcoming = valid.filter((s) => {
    const end = moment(s.end_at);
    return (
      (end.isValid() ? end.valueOf() : moment(s.start_at).valueOf()) > nowMs
    );
  });

  return earliestStart(upcoming.length ? upcoming : valid);
};
